import { DashboardUseCaseModelCount, DashboardSeverityKpi, DashboardUseCaseCounts, DashboardAiModelCount } from '@api';
import { withPageBase } from '@components/layout';
import { CircleLoader, Title, TabsMenu } from '@components/ui';
import { useApi } from '@data-access';
import { Portfolio } from '@sections/DashboardCharts/_components';
import { ActiveFilters } from '@sections/DashboardCharts/_components/ActiveFilters/ActiveFilters';
import { DoughnutChartProb } from '@sections/DashboardCharts/_components/DoughnutChart/DoughnutChartProb';
import { Export } from '@sections/DashboardCharts/_components/Export/Export';
import { Filters } from '@sections/DashboardCharts/_components/Filters/Filters';
import { RiskExposureProb } from '@sections/DashboardCharts/_components/RiskExposure/RiskExposureProb';
import { RiskIndicatorsProb } from '@sections/DashboardCharts/_components/RiskIndicators/RiskIndicatorsProb';
import { RiskRadarProb } from '@sections/DashboardCharts/_components/RiskRadar/RiskRadarProb';
import { TwoDimensionsChart } from '@sections/DashboardCharts/_components/TwoDimensionsChart/TwoDimensionsChart';
import { notionStore } from '@stores/notion.store';
import { settingsStore } from '@stores/settings.store';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import Router, { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './Dashboard.module.scss';

interface QuantityType extends DashboardAiModelCount, DashboardUseCaseCounts {
  usecases: DashboardUseCaseModelCount[];
  useCasesTotal: number;
}

export interface SelectedFiltersType {
  departments: string[];
  useCases: string[];
  models: string[];
  untilDate: Date;
}

export interface FiltersType {
  departments: string[];
  useCases: { name: string; models: string[] }[];
  untilDate: Date;
}

export interface DashboardApiFilterParameters {
  aiModels?: Array<string>;
  date?: string;
  departments?: Array<string>;
  limit?: number;
  useCases?: Array<string>;
}

const FILTER_INIT = { untilDate: new Date(), departments: [], useCases: [], models: [] };

type TabId = 'governance' | 'model-dashboard';

const Dashboard = observer(() => {
  const { api, dashboardApi } = useApi();

  const { values } = notionStore;
  const { riskLimits } = settingsStore;
  // charts data
  const [dashboardSeverityKpi, setDashboardSeverityKpi] = useState<DashboardSeverityKpi>();
  const [quantity, setQuantity] = useState<QuantityType>();

  // tabs
  const [activeTab, setActiveTab] = useState<TabId>('governance');

  // filters
  const [filters, setFilters] = useState<FiltersType>({
    untilDate: new Date(),
    departments: [],
    useCases: [],
  });

  const router = useRouter();

  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>(FILTER_INIT);
  const [appliedFilters, setAppliedFilters] = useState<SelectedFiltersType>({
    departments: (router.query?.departments as string)?.split(',') || [],
    models: (router.query?.models as string)?.split(',') || [],
    untilDate: dayjs(router.query?.date as string).toDate(),
    useCases: (router.query?.usecases as string)?.split(',') || [],
  });

  // 1. graph Risk Exposure
  const fetchSeverityKpis = useCallback(
    async (params?: DashboardApiFilterParameters) => {
      try {
        const kpis = await dashboardApi.apiDashboardPortfolioTotalRiskRetrieve(params);
        setDashboardSeverityKpi(kpis);
      } catch (error) {
        console.log(error);
      }
    },
    [dashboardApi],
  );

  // 2. graph Portfolio
  const fetchModelUsecaseQuantity = useCallback(
    async params => {
      try {
        const { results, useCaseChart, aiModelChart } = await dashboardApi.apiDashboardPortfolioTotalCountsRetrieve(
          params,
        );
        if (results) {
          const sortedResults = results.sort((a, b) => (a.month > b.month ? 1 : -1));
          setQuantity({
            useCasesTotal: sortedResults[sortedResults.length - 1].useCaseCount,
            usecases: sortedResults,
            inReviewModelCount: aiModelChart?.inReviewModelCount,
            liveModelCount: aiModelChart?.liveModelCount,
            notLiveModelCount: aiModelChart?.notLiveModelCount,
            euAiActRiskClass: useCaseChart?.euAiActRiskClass,
            materiality: useCaseChart?.materiality,
            readiness: useCaseChart?.readiness,
            riskAssessment: useCaseChart?.riskAssessment,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [dashboardApi],
  );

  const fetchData = useCallback(
    (params?: DashboardApiFilterParameters) => {
      Promise.all([fetchSeverityKpis(params), fetchModelUsecaseQuantity(params)]);
    },
    [fetchSeverityKpis, fetchModelUsecaseQuantity],
  );

  const fetchFilters = useCallback(async () => {
    try {
      const [departments, tree] = await Promise.all([
        api.apiDepartmentsList(),
        dashboardApi.apiDashboardFilterTreeDataRetrieve(),
      ]);

      setFilters({
        departments: departments?.results?.map(department => department.name) || [],
        useCases: tree.results,
        untilDate: new Date(),
      });
    } catch (error) {
      console.log(error);
    }
  }, [api, dashboardApi]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // update filters query in URL
  const updateQuery = useCallback(() => {
    let url = `${
      !dayjs(appliedFilters.untilDate).isSame(new Date(), 'day')
        ? `date=${dayjs(appliedFilters.untilDate).format('YYYY-MM')}`
        : ''
    }${appliedFilters.models.length > 0 ? `&models=${appliedFilters.models.join(',')}` : ''}${
      appliedFilters.useCases.length > 0 ? `&usecases=${appliedFilters.useCases.join(',')}` : ''
    }${appliedFilters.departments.length > 0 ? `&departments=${appliedFilters.departments.join(',')}` : ''}`;

    if (url.length !== 0) {
      url = `?${url}`.replace('?&', '?');
    }
    Router.push(url, undefined, {
      shallow: true,
    });
  }, [appliedFilters]);

  useEffect(() => {
    fetchData({
      aiModels: appliedFilters.models?.length > 0 ? appliedFilters.models : undefined,
      date: appliedFilters.untilDate ? dayjs(appliedFilters.untilDate).format('YYYY-MM').toString() : undefined,
      departments: appliedFilters.departments?.length > 0 ? appliedFilters.departments : undefined,
      useCases: appliedFilters.useCases?.length > 0 ? appliedFilters.useCases : undefined,
    });
    setSelectedFilters(appliedFilters);
    updateQuery();
  }, [appliedFilters, updateQuery, fetchData]);

  const filtersActive = useMemo(
    () => ({
      aiModels: appliedFilters.models?.length > 0 ? appliedFilters.models : undefined,
      date: appliedFilters.untilDate ? dayjs(appliedFilters.untilDate).format('YYYY-MM').toString() : undefined,
      departments: appliedFilters.departments?.length > 0 ? appliedFilters.departments : undefined,
      useCases: appliedFilters.useCases?.length > 0 ? appliedFilters.useCases : undefined,
    }),
    [appliedFilters],
  );

  const tabsList = [
    { id: 'governance', name: 'Governance' },
    { id: 'model-dashboard', name: 'Test Analytics' },
  ];

  if (!quantity || !riskLimits)
    return (
      <div className={styles.loaderWrapper}>
        <CircleLoader />
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title
          className={styles.title}
          htmlElement="h1"
          looksLike="h2"
        >
          {activeTab === 'governance' ? 'Governance' : 'Test Analytics'}
        </Title>
        <div className={styles.controls}>
          <Filters
            filters={filters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
          <Export />
        </div>
      </div>
      <ActiveFilters
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
      <TabsMenu
        className={styles.tabs}
        activeTabId={activeTab}
        list={tabsList}
        onChange={(id: TabId) => setActiveTab(id)}
      />
      {activeTab === 'governance' && (
        <div className={styles.top}>
          <div className={styles.left}>
            <RiskExposureProb kpis={dashboardSeverityKpi} />
            <TwoDimensionsChart
              appliedFilters={filtersActive}
              riskAssessment={quantity?.riskAssessment}
            />
          </div>
          <div className={styles.right}>
            <Portfolio
              useCasesTotal={quantity?.useCasesTotal}
              useCases={quantity?.usecases}
              liveModels={quantity?.liveModelCount}
              inReviewModels={quantity?.inReviewModelCount}
              notLiveModels={quantity?.notLiveModelCount}
              euAiActRiskClass={quantity?.euAiActRiskClass}
              materiality={quantity?.materiality}
              readiness={quantity?.readiness}
            />
          </div>
        </div>
      )}
      {activeTab === 'model-dashboard' && (
        <>
          <RiskIndicatorsProb appliedFilters={appliedFilters} />
          <div className={styles.grid}>
            <RiskRadarProb appliedFilters={filtersActive} />
            <DoughnutChartProb appliedFilters={filtersActive} />
          </div>
        </>
      )}
    </div>
  );
});

export default withPageBase(Dashboard);
